import { SceneDescriptor, SceneObjectDescriptor } from '../types'
import { killAvatarOnTouch } from './primitives/killAvatarOnTouch'
import {
  BehaviorHandler,
  MissionBehaviors,
  MissionBehaviorsFactory,
} from './types'

const DEFAULT_WAIT_SECONDS = 0

const LUA_COMMENT_RE = /--[^\n\r]*/g
const WHITESPACE_RE = /\s+/g
const TOUCH_CONNECT_RE = /(?:^|[\s.])Touched\s*:\s*Connect\s*\(/i
const HUMANOID_RE = /FindFirstChild\s*\(\s*["']Humanoid["']\s*\)/i
const HUMANOID_HEALTH_ZERO_RE = /\.Health\s*=\s*0\b/i
const PARENT_ALIAS_RE =
  /(?:^|\s)(?:local\s+)?([A-Za-z_]\w*)\s*=\s*script\s*\.\s*Parent\b/gi
const DIRECT_PARENT_DESTROY_RE = /script\s*\.\s*Parent\s*:\s*Destroy\s*\(\s*\)/i
const WAIT_BEFORE_PARENT_DESTROY_RE =
  /^\s*wait\s*\(\s*([0-9]+(?:\.[0-9]+)?)?\s*\)\s*;?\s*script\s*\.\s*Parent\s*:\s*Destroy\s*\(\s*\)\s*$/i
const WALK_SPEED_RE = /\.WalkSpeed\s*=\s*([0-9]+(?:\.[0-9]+)?)\b/i
const JUMP_POWER_RE = /\.JumpPower\s*=\s*([0-9]+(?:\.[0-9]+)?)\b/i

const normalizeSource = (source: string): string =>
  source.replace(LUA_COMMENT_RE, '').replace(WHITESPACE_RE, ' ')

const readNumber = (source: string, pattern: RegExp): number | undefined => {
  const match = pattern.exec(source)
  if (!match?.[1]) {
    return undefined
  }
  const value = Number(match[1])
  return Number.isFinite(value) ? value : undefined
}

const chainHandlers =
  (handlers: readonly BehaviorHandler[]): BehaviorHandler =>
  async (ctx) => {
    for (const handler of handlers) {
      if (ctx.signal.aborted) {
        return
      }
      await handler(ctx)
    }
  }

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const hasParentDestroy = (source: string): boolean => {
  if (DIRECT_PARENT_DESTROY_RE.test(source)) {
    return true
  }
  PARENT_ALIAS_RE.lastIndex = 0
  for (let match = PARENT_ALIAS_RE.exec(source); match !== null; ) {
    const alias = match[1]
    const destroyRe = new RegExp(
      `\\b${escapeRegExp(alias)}\\s*:\\s*Destroy\\s*\\(\\s*\\)`,
      'i',
    )
    if (destroyRe.test(source)) {
      return true
    }
    match = PARENT_ALIAS_RE.exec(source)
  }
  return false
}

const createTouchHandler = (
  object: SceneObjectDescriptor,
  source: string,
): BehaviorHandler | undefined => {
  if (!TOUCH_CONNECT_RE.test(source) || !HUMANOID_RE.test(source)) {
    return undefined
  }

  const handlers: BehaviorHandler[] = []
  const jumpPower = readNumber(source, JUMP_POWER_RE)
  const walkSpeed = readNumber(source, WALK_SPEED_RE)

  if (HUMANOID_HEALTH_ZERO_RE.test(source)) {
    handlers.push(killAvatarOnTouch)
  }
  if (jumpPower !== undefined) {
    handlers.push(({ world }) => world.setAvatarJumpPower(jumpPower))
  }
  if (walkSpeed !== undefined) {
    handlers.push(({ world }) => world.setAvatarWalkSpeed(walkSpeed))
  }
  if (hasParentDestroy(source)) {
    handlers.push(({ world }) => world.destroy(object.id))
  }

  if (handlers.length === 0) {
    return undefined
  }
  return handlers.length === 1 ? handlers[0] : chainHandlers(handlers)
}

const createDelayedDestroyHandler = (
  object: SceneObjectDescriptor,
  source: string,
): BehaviorHandler | undefined => {
  const match = WAIT_BEFORE_PARENT_DESTROY_RE.exec(source)
  if (!match) {
    return undefined
  }
  const seconds =
    match[1] !== undefined ? Number(match[1]) : DEFAULT_WAIT_SECONDS
  if (!Number.isFinite(seconds) || seconds < 0) {
    return undefined
  }
  return async ({ world, wait, signal }) => {
    await wait(seconds)
    if (!signal.aborted) {
      world.destroy(object.id)
    }
  }
}

/**
 * rbxl から取り込んだ Script/LocalScript のうち、プレビューで安全に再現できる
 * 既知パターンだけを MissionBehaviors に変換する。
 *
 * Luau の任意実行はしない。対応範囲はテンプレートで頻出する小さな Script に限定する:
 * - Touched + Humanoid.Health=0: アバター死亡
 * - Touched + Humanoid.WalkSpeed/JumpPower 代入: アバター能力値変更
 * - Touched + script.Parent:Destroy(): 接触した部品を削除
 * - wait(n) + script.Parent:Destroy(): プレイ開始後 n 秒で削除
 */
export const scriptBehaviorsForScene = (
  descriptor: SceneDescriptor,
): MissionBehaviorsFactory | undefined => {
  const onPlay: BehaviorHandler[] = []
  const onTouchCharacter = new Map<string, BehaviorHandler>()

  for (const object of descriptor.objects) {
    for (const script of object.scripts ?? []) {
      if (script.className !== 'Script' || script.disabled === true) {
        continue
      }
      const source = normalizeSource(script.source)
      const touchHandler = createTouchHandler(object, source)
      if (touchHandler) {
        const current = onTouchCharacter.get(object.name)
        onTouchCharacter.set(
          object.name,
          current ? chainHandlers([current, touchHandler]) : touchHandler,
        )
      }
      const delayedDestroy = createDelayedDestroyHandler(object, source)
      if (delayedDestroy) {
        onPlay.push(delayedDestroy)
      }
    }
  }

  if (onPlay.length === 0 && onTouchCharacter.size === 0) {
    return undefined
  }

  return (): MissionBehaviors => ({
    onPlay: onPlay.length > 0 ? onPlay : undefined,
    onTouchCharacter: onTouchCharacter.size > 0 ? onTouchCharacter : undefined,
  })
}
